import { useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { EMPTY_OBJECT } from '../../../constants/common'
import { useMinimizedModalsDirtyGuard } from '../../../hooks'
import { isDirtyForm } from '../../../utils/formHelpers'
import { isDraftSaveAction } from '../../Draft'
import { MINIMIZED_MODAL_KEY, useMinimizableModal } from '../../Modal'
import { EMAIL_MINIMIZED_REPLACE_CONFIRMATION } from '../Email.constants'
import { useEmailDraft, useEmailForm } from '../Email.hooks'
import { EmailCreationForm } from '../EmailCreationForm'
import { EmailCreationFormProvider } from '../EmailCreationFormProvider'
import { EmailCreationPopupContainer } from '../EmailCreationPopupContainer'
import { EmailDraftCloseActionSelectModal } from '../EmailDraftCloseActionSelectModal'

import { QA_NAME_EMAIL_CREATION_MODAL } from './EmailCreationPopUp.constants'

function EmailCreationPopUp({
  FormContainer = EmailCreationPopupContainer,
  containerProps,
  getContainerIsDirty,
  getIsDirty = isDirtyForm,
  initialValues,
  isLeaveGuardEnabled = false,
  minimizedModalKey = MINIMIZED_MODAL_KEY.EMAIL_FORM,
  minimizedModalScope,
  minimizedModalSourceId,
  resizeConfig: { initialFullScreen = true, ...resizeConfig } = EMPTY_OBJECT,
  title = 'Modal.NewEmailTitleKey',
  onClose,
  ...restProps
}) {
  const { t } = useTranslation()

  const {
    blackList,
    checkContainerIsDirty,
    draggingRef,
    fullScreen,
    handleClose,
    handleCloseModal,
    handleDragOver,
    handleDropFile,
    handleResizeModal,
    isCloseConfirm,
    isDragging,
    isMobileScreenSize,
    modal,
    setBlackList,
    setCloseConfirm,
    setModal,
  } = useEmailForm({
    getContainerIsDirty,
    getIsDirty,
    resizeConfig: { ...resizeConfig, initialFullScreen },
    onClose,
  })

  const { setDraft: setEmailDraft } = useEmailDraft()

  const closeConfirmation = {
    Component: EmailDraftCloseActionSelectModal,
    onSubmit: action => {
      if (!isDraftSaveAction(action)) {
        setEmailDraft(null, 0)
      }
    },
  }

  const { handleMinimize, isMinimized, setIsDirty } = useMinimizableModal({
    closeConfirmation,
    minimizeKey: minimizedModalKey,
    qaName: QA_NAME_EMAIL_CREATION_MODAL,
    replaceConfirmation: EMAIL_MINIMIZED_REPLACE_CONFIRMATION,
    scope: minimizedModalScope,
    sourceId: minimizedModalSourceId,
    title: t(title),
    onClose,
  })

  const isDirtyRef = useRef(false)

  function handleIsDirtyChange(value) {
    isDirtyRef.current = !!value

    setIsDirty(value)
  }

  function getIsOpenFormDirty() {
    return isDirtyRef.current
  }

  // While minimized the tray entry covers the dirty check instead.
  useMinimizedModalsDirtyGuard({
    getIsDirty: getIsOpenFormDirty,
    isEnabled: isLeaveGuardEnabled && !isMinimized,
    leaveConfirmation: closeConfirmation,
    scope: minimizedModalScope,
  })

  return (
    <EmailCreationFormProvider
      blackList={blackList}
      getIsDirty={getIsDirty}
      isDragging={isDragging}
      isFullScreen={isMobileScreenSize || fullScreen}
      setBlackList={setBlackList}
      setModal={setModal}
      onCloseModal={handleCloseModal}
      onDragOver={handleDragOver}
      onDrop={handleDropFile}
      onResize={handleResizeModal}
    >
      <EmailCreationForm
        {...restProps}
        FormContainer={FormContainer}
        containerProps={{
          ...containerProps,
          boxProps: {
            ...containerProps?.boxProps,
            ref: draggingRef,
          },
          getIsDirty: checkContainerIsDirty,
          hidden: isMinimized,
          isCloseConfirm,
          isFullScreen: isMobileScreenSize || fullScreen,
          modal,
          qaName: QA_NAME_EMAIL_CREATION_MODAL,
          setCloseConfirm,
          title,
          onClose,
          onIsDirtyChange: handleIsDirtyChange,
          onMinimize: handleMinimize,
          onResize: handleResizeModal,
        }}
        initialValues={initialValues}
        onCancel={handleClose}
      />
    </EmailCreationFormProvider>
  )
}

export default EmailCreationPopUp
